import { cn } from 'utils/styles';
import { CalloutColumns, CopyJustification, KeyLinePosition } from 'types/constants';
import { CalloutGridCardProps } from './types';

/**
 * Configuration object mapping CalloutColumns to their grid and border classes
 */
export const CALLOUT_GRID_CONFIG = {
  [CalloutColumns.Two]: {
    grid: 'sm:grid-cols-2',
    border: 'sm:nth-[2n]:border-r-0',
  },
  [CalloutColumns.Three]: {
    grid: 'sm:grid-cols-2 md:grid-cols-3',
    border: 'max-md:nth-[2n]:border-r-0 md:nth-[3n]:border-r-0',
  },
  [CalloutColumns.Four]: {
    grid: 'sm:grid-cols-2 md:grid-cols-4',
    border: 'max-md:nth-[2n]:border-r-0 md:nth-[4n]:border-r-0',
  },
  [CalloutColumns.Five]: {
    grid: 'sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5',
    border: 'max-md:nth-[2n]:border-r-0 md:max-lg:nth-[3n]:border-r-0 lg:nth-[5n]:border-r-0',
  },
} as const;

export const CALLOUT_GRID_NUM_ITEMS = {
  [CalloutColumns.Two]: 2,
  [CalloutColumns.Three]: 3,
  [CalloutColumns.Four]: 4,
  [CalloutColumns.Five]: 5,
};

/**
 * Generates CSS classes for the grid container based on column count and variant
 * @param columns - Number of columns to display
 * @param isIconVariant - Whether this is an icon variant (affects spacing)
 * @param isBasicVariant - Whether this is a basic variant (affects spacing)
 * @param keyline - Position of keyline (affects spacing)
 * @returns Tailwind CSS classes for grid layout
 */
export const getGridClasses = (
  columns: CalloutColumns,
  isIconVariant: boolean,
  isBasicVariant: boolean,
  keyline?: KeyLinePosition,
) => {
  const hasInBetweenKeyline = keyline === KeyLinePosition.Between;
  return cn(
    'grid grid-cols-1 gap-6 w-full',
    CALLOUT_GRID_CONFIG[columns].grid,
    isBasicVariant ? 'md:gap-y-24' : 'md:gap-y-16',
    { 'md:gap-y-20': isIconVariant && !hasInBetweenKeyline, 'md:gap-y-12': isIconVariant && hasInBetweenKeyline },
  );
};

/**
 * Gets the border removal classes for a specific column configuration
 * @param columns - Number of columns to display
 * @returns Tailwind CSS classes for border removal
 */
export const getBorderClasses = (columns: CalloutColumns) => {
  return cn(CALLOUT_GRID_CONFIG[columns].border);
};

/**
 * Generates CSS classes for the flex wrapper that contains icon/image and content
 * @param isIconVariant - Whether this is an icon variant (affects layout direction)
 * @param copyJustification - Global copy justification setting
 * @param isSingleContentItem - Whether the item has only one content element
 * @returns Tailwind CSS classes for flex layout
 */
export const getFlexWrapperClasses = (
  isIconVariant: boolean,
  copyJustification: CopyJustification | undefined,
  isSingleContentItem: boolean,
) => {
  return cn(
    `flex ${isIconVariant ? 'flex-row' : 'flex-col'} sm:flex-col max-sm:gap-x-7`,
    !isIconVariant && 'max-sm:gap-y-4',
    copyJustification === CopyJustification.Center ? 'items-center' : '',
    isIconVariant && isSingleContentItem ? 'max-sm:items-center' : '',
  );
};

/**
 * Determines if a card item has only one content element (headline, copy, or attribution)
 * This affects alignment behavior, especially for icon variants
 * @param item - The card item to check
 * @returns True if the item has exactly one content element
 */
export const isSingleContentItem = (item: CalloutGridCardProps) => {
  return [item.headline, item.headlineSmallRte, item.copy, item.attributionRte].filter(Boolean).length === 1;
};

/**
 * Determines whether to show a keyline under the main headline
 * Special case: Basic variant with "between" keyline position doesn't show keyline under headline
 * @param keyline - The keyline position setting
 * @param isBasicVariant - Whether this is a basic variant
 * @returns True if a keyline should be shown under the headline
 */
export const shouldShowKeylineUnderHeadline = (keyline: KeyLinePosition | undefined, isBasicVariant: boolean) => {
  return (
    (keyline === KeyLinePosition.Between || keyline === KeyLinePosition.Top) &&
    !(isBasicVariant && keyline === KeyLinePosition.Between)
  );
};
